import { useEffect, useRef, useState } from "react";
import { manageHeroPlayback } from "../lib/heroPlayback";
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { ASSETS, HERO } from "@/data/content";
import { Eyebrow, GoldButton } from "@/components/Section";
import { Blob } from "@/components/Decor";

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({ y: 0, transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] } }),
};
const fadeVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay) => ({ opacity: 1, y: 0, transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] } }),
};

const MaskedLine = ({ children, index }) => (
  <span className="block overflow-hidden pb-[0.1em] -mb-[0.1em] lg:whitespace-nowrap">
    <motion.span className="block" custom={index} variants={lineVariant} initial="hidden" animate="visible">
      {children}
    </motion.span>
  </span>
);

const VideoFrame = () => {
  const ref = useRef(null);
  const [fallback, setFallback] = useState(false);
  const reduceMotion = useReducedMotion();
  const spring = { stiffness: 175, damping: 24, mass: 0.8 };
  const rotateX = useSpring(0, spring);
  const rotateY = useSpring(0, spring);
  const lift = useSpring(0, spring);
  const scale = useSpring(1, spring);
  const canHover = () => !reduceMotion && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const onMove = (event) => {
    if (!canHover()) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = Math.max(-0.5, Math.min(0.5, (event.clientX - bounds.left) / bounds.width - 0.5));
    const y = Math.max(-0.5, Math.min(0.5, (event.clientY - bounds.top) / bounds.height - 0.5));
    const tilt = Math.min(1, 0.5 / Math.hypot(x, y));
    rotateX.set(-y * tilt * 18);
    rotateY.set(x * tilt * 18);
  };
  const onEnter = () => {
    if (!canHover()) return;
    lift.set(-8);
    scale.set(1.008);
  };
  const onLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
    lift.set(0);
    scale.set(1);
  };
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    return manageHeroPlayback(v, setFallback);
  }, []);

  return (
    <motion.div
      data-testid="hero-video-card"
      className="group relative isolate will-change-transform"
      style={{ rotateX, rotateY, y: lift, scale, transformStyle: "preserve-3d" }}
      onMouseMove={onMove}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div aria-hidden="true" className="pointer-events-none absolute -inset-7 rounded-[44px] bg-gradient-to-br from-[#D9C89E]/35 via-[#C8D4DF]/25 to-[#EEE7D8]/25 blur-2xl" />
      <div data-testid="hero-video-backplate-secondary" aria-hidden="true" className="pointer-events-none absolute -inset-[8px] translate-x-[7px] -translate-y-[11px] -rotate-[1.4deg] hidden rounded-[38px] bg-gradient-to-br from-[#F9F6F0] via-[#ECEFF0] to-[#DFE7E9] opacity-[0.85] shadow-[0_30px_65px_-34px_rgba(14,27,49,0.18),0_70px_115px_-50px_rgba(44,74,111,0.12)] lg:block" />
      <div aria-hidden="true" className="pointer-events-none absolute -inset-[7px] -translate-x-[6px] -translate-y-[6px] hidden rounded-[36px] bg-gradient-to-br from-[#E5D9C9]/90 via-[#DDE4E7]/85 to-[#D3DEE4]/80 shadow-[0_30px_70px_-35px_rgba(14,27,49,0.18),0_75px_115px_-55px_rgba(44,74,111,0.14)] lg:block" />
      <div aria-hidden="true" className="pointer-events-none absolute -inset-3 translate-x-3 translate-y-3 rounded-[34px] bg-gradient-to-br from-[#EEE7D8] via-[#E5E9E9] to-[#D4DEE5] shadow-[0_35px_70px_-35px_rgba(14,27,49,0.3)] ring-1 ring-[#B6A989]/20 lg:-inset-[8px] lg:translate-x-[8px] lg:translate-y-[9px] lg:rounded-[32px] lg:from-[#F4F0E9] lg:via-[#E9ECEC] lg:to-[#DDE5E9] lg:shadow-[0_24px_48px_-25px_rgba(14,27,49,0.18),0_55px_90px_-42px_rgba(44,74,111,0.16)] lg:ring-white/70" />
      <div className="relative overflow-hidden rounded-[26px] bg-[#0E1B31] shadow-[0_12px_24px_-12px_rgba(14,27,49,0.30),0_42px_85px_-32px_rgba(14,27,49,0.42),0_90px_150px_-55px_rgba(44,74,111,0.34)] ring-1 ring-white/60 transition-shadow duration-500 ease-out group-hover:shadow-[0_16px_30px_-12px_rgba(14,27,49,0.34),0_52px_100px_-32px_rgba(14,27,49,0.46),0_105px_165px_-55px_rgba(44,74,111,0.37)]">
        <video
          ref={ref}
          data-testid="hero-video"
          className="block w-full aspect-video object-cover"
          poster={ASSETS.poster}
          autoPlay
          muted
          playsInline
          preload="auto"
        >
          <source src={ASSETS.video} type="video/mp4" />
          <source src={ASSETS.videoWebm} type="video/webm" />
        </video>
        {fallback && (
          <img
            data-testid="hero-video-fallback"
            src={ASSETS.poster}
            alt=""
            aria-hidden="true"
            draggable={false}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
          />
        )}
      </div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative mesh-hero grain overflow-x-clip pt-32 lg:pt-40 xl:pt-[100px] pb-28 lg:pb-[72px]">
      <div className="perspective-grid" />
      <Blob color="rgba(194,109,82,0.16)" className="-top-40 right-[5%]" size={620} />
      <Blob color="rgba(90,114,101,0.16)" className="bottom-[-20%] left-[-10%]" size={560} />

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 lg:pl-10 xl:pl-[100px] lg:pr-6">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.14fr)] xl:grid-cols-[minmax(0,620px)_minmax(0,1fr)] gap-14 lg:gap-8 items-center">
          <motion.div style={{ y: textY }} className="relative z-10 min-w-0">
            <motion.div variants={fadeVariant} custom={0.1} initial="hidden" animate="visible">
              <Eyebrow tone="#96523F" className="font-semibold [&>span]:hidden">ΣΤΡΑΤΗΓΙΚΗ · ΑΝΑΠΤΥΞΗ · ΣΥΣΤΗΜΑΤΑ · AI</Eyebrow>
            </motion.div>
            <h1 className="mt-8 lg:mt-6 font-display text-[2rem] sm:text-5xl lg:text-[clamp(2.25rem,3.34vw,3rem)] xl:text-[3.25rem] font-semibold leading-[1.02] lg:leading-[1.79] xl:leading-[1.3] tracking-[-0.035em] lg:tracking-[-0.015em] xl:tracking-[-0.02em] text-[#0E1B31] lg:w-[calc(100%+35px)]">
              <MaskedLine index={0}>Βάζουμε τάξη στον</MaskedLine>
              <MaskedLine index={1}>τρόπο με τον οποίο</MaskedLine>
              <MaskedLine index={2}>
                <span className="italic text-[#C26D52]">λειτουργεί</span>{" "}
                <span className="italic text-[#2C4A6F]">και αναπτύσσεται</span>
              </MaskedLine>
              <MaskedLine index={3}>η επιχείρησή σας</MaskedLine>
            </h1>
            <motion.p variants={fadeVariant} custom={0.95} initial="hidden" animate="visible" className="mt-8 lg:mt-7 text-base sm:text-[1.1875rem] leading-relaxed lg:leading-[1.55] text-[#334155] lg:text-[#243247] max-w-xl lg:max-w-[34rem]">
              {HERO.subtitle}
            </motion.p>
            <motion.p variants={fadeVariant} custom={1.1} initial="hidden" animate="visible" className="mt-4 lg:mt-5 font-display text-2xl sm:text-[1.7rem] font-medium italic text-[#0E1B31] flex items-start gap-3">
              <span aria-hidden="true" className="mt-[0.7em] h-px w-12 shrink-0 bg-gradient-to-r from-[#C26D52] to-[#C5A059]" />
              <span className="block min-w-0 pb-[0.15em] leading-[1.25]">{HERO.closing}</span>
            </motion.p>
            <motion.div variants={fadeVariant} custom={1.25} initial="hidden" animate="visible" className="mt-7 flex flex-wrap gap-3">
              <GoldButton to="/contact" testId="hero-cta-button">Ξεκινήστε τη συζήτηση</GoldButton>
              <GoldButton to="/services" testId="hero-services-button" variant="outline">Εξερευνήστε τις υπηρεσίες</GoldButton>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative min-w-0"
          >
            <motion.div style={{ y: videoY }} className="relative w-full lg:-top-[105px] xl:-top-[10px]">
              <div className="lg:ml-[2.5%] lg:w-[88.35%] xl:w-[84%] lg:[perspective:1000px]">
                <div className="lg:[transform:rotateY(-2deg)]">
                  <VideoFrame />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
